<template>
  <div
    class="data-table"
    :class="isRowClickable ? 'table-pointer' : ''"
  >
    <slot name="header" />
    <!-- Optional client-side search bar -->
    <div
      v-if="searchable && !serverMode"
      class="table-header mb-2 d-flex justify-content-end"
    >
      <div
        class="table-search"
        style="min-width: 260px"
      >
        <b-input-group size="md">
          <b-input-group-prepend
            is-text
            style="height: 100%"
          >
            <b-icon
              icon="search"
              color="#003478"
            ></b-icon>
          </b-input-group-prepend>
          <b-form-input
            type="search"
            v-model="localSearchTerm"
            @input="handleSearchInput"
            :placeholder="$t('bopModelSetup.filter').toString()"
            :aria-label="$t('bopModelSetup.filter').toString()"
          ></b-form-input>
        </b-input-group>
      </div>
    </div>
    <div
      v-if="api.loading"
      class="text-center p-3"
    >
      <b-spinner
        label="loading"
        variant="primary"
      />
    </div>
    <table
      v-else
      class="table table-sm table-hover vuetable"
    >
      <thead>
        <tr>
          <th
            v-for="col in columns"
            :key="col.key"
            @click="col.sortable === false ? null : onSort(col)"
            :class="[
              col.sortable === false ? '' : sortClass(col),
              col.key === 'bulk' ? 'col-bulk' : '',
            ]"
          >
            <slot
              :name="'header-' + col.key"
              :col="col"
            >
              <span
                v-if="col.key === 'bulk'"
                class="bulk-header-wrapper"
                v-html="col.label"
              ></span>
              <span v-else>{{ col.label }}</span>
            </slot>
            <span v-if="col.sortable !== false && isSorted(col)">
              <b-icon
                :icon="
                  api.sortState?.direction === 'asc'
                    ? 'chevron-up'
                    : 'chevron-down'
                "
              />
            </span>
          </th>
          <th v-if="showActions">{{ actionsLabel }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="row in api.pageRows"
          :key="track(row)"
          @click="emitRow(row)"
          :class="rowClass ? rowClass(row) : ''"
        >
          <td
            v-for="col in columns"
            :key="col.key + '-cell'"
            :class="col.key === 'bulk' ? 'col-bulk bulk-cell' : ''"
          >
            <slot
              :name="col.slot"
              :row="row"
              :value="resolve(row, col.key)"
              :col="col"
            >
              <EditField
                :props="{
                  rowData: row,
                  rowField: {
                    property: col.key,
                    isNotEditable: true,
                    isTableCell: true,
                  },
                }"
              />
            </slot>
          </td>
          <td v-if="showActions">
            <slot
              name="actions"
              :row="row"
            />
          </td>
        </tr>
        <tr v-if="!api.pageRows.length">
          <td
            :colspan="columns.length + (showActions ? 1 : 0)"
            class="text-center py-3 text-muted"
          >
            {{ $t("table.noDataAvailable") }}
          </td>
        </tr>
      </tbody>
    </table>
    <div
      class="table-pagination"
      v-if="api.pagination.pageCount > 1"
      style="padding-top: 10px; width: fit-content"
    >
      <div class="vuetable-pagination pull-right">
        <a
          href="#"
          class="btn btn-border"
          :class="{ disabled: api.pagination.page === 1 }"
          @click.prevent="api.setPage(1)"
          >«</a
        >
        <a
          href="#"
          class="btn btn-border"
          :class="{ disabled: api.pagination.page === 1 }"
          @click.prevent="api.setPage(api.pagination.page - 1)"
          >‹</a
        >
        <a
          v-for="p in pages"
          :key="'page-' + p"
          href="#"
          :aria-current="p === api.pagination.page ? 'page' : undefined"
          :class="[
            'btn',
            p === api.pagination.page ? 'btn-primary' : 'btn-border',
          ]"
          @click.prevent="api.setPage(p)"
          >{{ p }}</a
        >
        <a
          href="#"
          class="btn btn-border"
          :class="{
            disabled: api.pagination.page === api.pagination.pageCount,
          }"
          @click.prevent="api.setPage(api.pagination.page + 1)"
          >›</a
        >
        <a
          href="#"
          class="btn btn-border"
          :class="{
            disabled: api.pagination.page === api.pagination.pageCount,
          }"
          @click.prevent="api.setPage(api.pagination.pageCount)"
          >»</a
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import { PropType } from "vue";
import { Guid } from "guid-typescript";
import { useDataTable, DataTableApi } from "@/composables/useDataTable";

interface SimpleColumnDef {
  key: string;
  label: string;
  slot?: string;
  sortField?: string; // optional separate field path for sorting (server & client)
  sortable?: boolean; // disable sort when false (e.g. bulk checkbox column)
}

export default Vue.extend({
  name: "DataTable",
  components: {
    EditField: () => import("@/components/Shared/Table/EditField.vue"),
  },
  props: {
    columns: { type: Array as PropType<SimpleColumnDef[]>, required: true },
    rows: { type: Array as PropType<Record<string, any>[]>, default: () => [] },
    perPage: { type: Number, default: 20 },
    trackBy: { type: String, default: "id" },
    showActions: { type: Boolean, default: false },
    actionsLabel: { type: String, default: "Actions" },
    url: { type: String, default: "" },
    serverMode: { type: Boolean, default: false },
    parseResponse: {
      type: Function as PropType<(resp: any) => { rows: any[]; total: number }>,
      default: undefined,
    },
    buildQueryParams: {
      type: Function as PropType<
        (state: {
          page: number;
          perPage: number;
          search: string;
          sort: any;
        }) => Record<string, any>
      >,
      default: undefined,
    },
    timeout: { type: Number, default: undefined },
    searchable: { type: Boolean, default: false },
    rowClass: {
      type: Function as PropType<
        (row: any) => string | Record<string, boolean> | any[]
      >,
      default: undefined,
    },
    isRowClickable: { type: Boolean, default: false },
  },
  data() {
    return {
      api: useDataTable({
        trackBy: this.trackBy,
        perPage: this.perPage,
        timeout: this.timeout,
        initialRows: this.rows,
        server: this.serverMode,
        parseResponse: this.parseResponse,
        buildQueryParams: this.buildQueryParams,
      }) as DataTableApi<any>,
      localSearchTerm: "" as string,
    };
  },
  created() {
    if (this.url) this.api.load(this.url);
  },
  computed: {
    pages(): number[] {
      const total = this.api.pagination.pageCount;
      const current = this.api.pagination.page;
      const WINDOW = 5;
      if (total <= WINDOW) {
        return Array.from({ length: total }, (_, i) => i + 1);
      }
      let start = current - Math.floor(WINDOW / 2);
      if (start < 1) start = 1;
      let end = start + WINDOW - 1;
      if (end > total) {
        end = total;
        start = end - WINDOW + 1;
      }
      const pages: number[] = [];
      for (let p = start; p <= end; p++) pages.push(p);
      return pages;
    },
  },
  methods: {
    // Access trackBy via cast to satisfy TS when inferring method context
    track(row: any) {
      // Defensive handling: ensure trackBy is a valid string
      const rawTrackBy: any = (this as any).trackBy;
      const trackKey: string =
        typeof rawTrackBy === "string" && rawTrackBy.length ? rawTrackBy : "id";
      let value: any;
      if (trackKey.indexOf(".") !== -1) {
        value = trackKey
          .split(".")
          .reduce(
            (acc: any, part: string) => (acc ? acc[part] : undefined),
            row,
          );
      } else {
        value = row ? row[trackKey] : undefined;
      }
      if (typeof value === "string" || typeof value === "number") return value;
      // Generate a stable synthetic key if needed
      if (!row.__dtKey) {
        row.__dtKey =
          row?.translation?.id || row?.site?.id || Guid.create().toString();
      }
      return row.__dtKey;
    },
    resolve(row: any, key: string) {
      // Support direct key
      if (!key || key.indexOf(".") === -1) return row ? row[key] : undefined;
      // Special handling for coefficient structures when row.coefficients is an array
      if (key.startsWith("coefficients.") && Array.isArray(row.coefficients)) {
        const parts = key.split(".");
        const coeffKey = parts[1];
        const found = row.coefficients.find(
          (c: any) => c.key === coeffKey || c.coefficientKey === coeffKey,
        );
        if (!found) return undefined;
        // Traverse remaining path parts on found object
        let val: any = found;
        for (let i = 2; i < parts.length; i++) {
          val = val ? val[parts[i]] : undefined;
        }
        return val;
      }
      // Generic nested path resolution
      return key
        .split(".")
        .reduce((acc: any, part: string) => (acc ? acc[part] : undefined), row);
    },
    emitRow(row: any) {
      this.$emit("row-clicked", row);
    },
    onSort(col: SimpleColumnDef) {
      const field = col.sortField || col.key;
      let dir: "asc" | "desc" = "asc";
      if (this.api.sortState && this.api.sortState.field === field) {
        dir = this.api.sortState.direction === "asc" ? "desc" : "asc";
      }
      this.api.sort(field, dir);
      this.$emit("sorted", { field, direction: dir });
    },
    isSorted(col: SimpleColumnDef) {
      const field = col.sortField || col.key;
      return this.api.sortState && this.api.sortState.field === field;
    },
    sortClass(col: SimpleColumnDef) {
      if (!this.isSorted(col)) return "sortable";
      return this.api.sortState?.direction === "asc"
        ? "sorted-asc"
        : "sorted-desc";
    },
    handleSearchInput() {
      if (!this.searchable || this.serverMode) return; // only client-side for now
      const term = this.localSearchTerm;
      if (!term) {
        this.api.clearSearch();
      } else {
        this.api.applySearch(term);
      }
    },
  },
});
</script>

<style lang="scss">
@import "@/../node_modules/bootstrap/scss/functions";
@import "@/../node_modules/bootstrap/scss/variables";
@import "@/../node_modules/bootstrap/scss/mixins";
@import "@/../node_modules/bootstrap/scss/modal";
@import "@/../node_modules/bootstrap/scss/close";
@import "@/../node_modules/bootstrap-vue/src/index.scss";

.data-table .vuetable thead th {
  vertical-align: middle;
}

.table-pointer {
  tbody > tr {
    cursor: pointer;
  }
}

.group-button-table .btn {
  max-height: 42px;
}
.duplicate-table .vuetable th {
  background-color: #586575a8;
}
.centered-bulk-chb {
  height: 19px;
  vertical-align: middle;
  display: flex;
  align-items: center;
  float: left;
}
.btn-icon {
  padding: 0.25rem;
  width: 2.4rem;
  height: 2.4rem;
  display: flex;
  align-items: center;
  justify-content: center;

  /* inner SVG */
  b-icon {
    font-size: 1.6rem !important;
  }
}
.col-bulk {
  width: 32px !important;
  text-align: center;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}
.bulk-cell {
  vertical-align: middle !important;
}
.bulk-header-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
}
.center-field-element {
  display: flex;
  margin: 0 auto;
  cursor: pointer;
}
@include media-breakpoint-down(sm) {
  .row > button {
    width: 100% !important;
    margin: 0.25em 0;
  }

  .input-group {
    width: 100% !important;
  }

  .vuetable-pagination > a {
    padding: 0.46875rem !important;
  }

  @media (max-width: 769px) {
    .group-button-table {
      flex-direction: column;

      button {
        max-height: 42px;
      }
    }
  }
}
</style>
